import { closeWorkspaceTab } from './closeWorkspaceTab'
import { parseTabPath } from './parseTabPath'
import { useAppWorkspaceStore } from './appWorkspaceStore'

/**
 * Close list/record tabs whose object is gone (module uninstall, stale session).
 * Reports/print tabs are left alone — they are not named by object.
 *
 * Returns a path to navigate to when `currentPath` itself was a closed tab;
 * otherwise null so a Modules-page uninstall does not yank the user away.
 */
export function pruneWorkspaceTabsForObjects(
  objectNames: Iterable<string>,
  currentPath?: string | null,
): string | null {
  const gone = new Set(
    Array.from(objectNames, (n) => n.trim()).filter(Boolean),
  )
  if (gone.size === 0) return null

  const { tabs } = useAppWorkspaceStore.getState()
  const closing = tabs.filter((t) => {
    const parsed = parseTabPath(t.path)
    return (
      (parsed?.kind === 'list' || parsed?.kind === 'record') &&
      Boolean(parsed.objectName) &&
      gone.has(parsed.objectName as string)
    )
  })
  if (closing.length === 0) return null

  const currentWasClosed = closing.some(
    (t) => t.path === currentPath || (currentPath != null && currentPath.startsWith(`${t.path}/`)),
  )

  let next: string | null = null
  for (const tab of closing) {
    next = closeWorkspaceTab(tab.path)
  }
  return currentWasClosed ? next : null
}

/**
 * After entities reload: drop list/record tabs whose object is not in the
 * live set. `knownNames` must be the full include_excluded registry.
 */
export function pruneWorkspaceTabsMissingFrom(
  knownNames: Iterable<string>,
  currentPath?: string | null,
): string | null {
  const known = new Set(
    Array.from(knownNames, (n) => n.trim()).filter(Boolean),
  )
  if (known.size === 0) return null

  const { tabs } = useAppWorkspaceStore.getState()
  const stale = new Set<string>()
  for (const t of tabs) {
    const parsed = parseTabPath(t.path)
    if (
      (parsed?.kind === 'list' || parsed?.kind === 'record') &&
      parsed.objectName &&
      !known.has(parsed.objectName)
    ) {
      stale.add(parsed.objectName)
    }
  }
  return pruneWorkspaceTabsForObjects(stale, currentPath)
}
